import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type { CardResolution, InsurerSummary, PaginatedResponse } from '@core/types/api';

function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }
  return (response as PaginatedResponse<T> | null)?.results ?? [];
}

/**
 * Insurers API.
 *
 * ORGANIZATION-scoped: `resolveCard` answers with this centre's status for the
 * insurer (enabled, credentials stored), so its cache must be dropped when the
 * active organization changes — it is listed in the store's `TENANT_SCOPED_APIS`.
 *
 * The card number posted to `resolveCard` is never stored, here or on the
 * server: the answer only ever carries the masked number.
 */
export const insurersApi = createApi({
  reducerPath: 'insurersApi',
  baseQuery,
  endpoints: (builder) => ({
    resolveCard: builder.mutation<CardResolution, string>({
      query: (raw) => ({
        url: '/api/insurers/resolve-card/',
        method: 'POST',
        body: { raw },
      }),
    }),
    listInsurers: builder.query<InsurerSummary[], void>({
      query: () => ({ url: '/api/insurers/', method: 'GET' }),
      transformResponse: (response: unknown) => unwrapList<InsurerSummary>(response),
    }),
  }),
});

export const { useResolveCardMutation, useListInsurersQuery } = insurersApi;
